import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckStepIcon } from "../icons/CheckStepIcon";
import { LoadingSpinner } from "../icons/LoadingSpinner";
import { ChartSnapshotIcon } from "../icons/ChartSnapshotIcon";
import { useNavigate } from "react-router-dom";
import TitleSection from "./TitleSection";
import AssetsStepBody from "./AssetsStepBody";
import PortfolioReadyBody from "./PortfolioReadyBody";

const STEP_STATE_STYLE = {
  completed: {
    circle: "bg-[#3c3c43] border-[#3c3c43] text-white",
    indicator: () => <CheckStepIcon className="text-white" />,
  },
  active: {
    circle: "bg-white border-[#C5C5C7] border-1",
    indicator: () => <LoadingSpinner />,
  },
  todo: {
    circle: "bg-white border-[#ECECED] text-[#A0A0A5]",
    indicator: (idx) => (
      <span className="text-[16px] font-bold">{idx + 1}</span>
    ),
  },
};

const LOADING_STEPS = [
  {
    id: "assets",
    label: "Chọn tài sản",
    title: "Nebula đã phân tích và chọn danh mục cho bạn",
    subtitle: "Dựa trên thông tin bạn đã xác nhận",
    Component: AssetsStepBody,
  },
  {
    id: "performance",
    label: "Tính hiệu suất",
    title: "Nếu bạn bắt đầu từ 01/2020:",
    subtitle: "So sánh danh mục Nebula với VN-Index trong 5 năm qua",
    Component: () => (
      <div className="w-full max-w-[462px] mx-auto">
        <ChartSnapshotIcon />
      </div>
    ),
  },
  {
    id: "ready",
    isReady: true,
    label: "Thiết lập xong",
    title: "Danh mục của bạn đã sẵn sàng!",
    subtitle:
      "Nebula đã xây dựng danh mục đa tài sản dựa trên thông tin phong cách đầu tư của bạn. Hôm nay thị trường chưa có tín hiệu đáng chú ý. Nebula sẽ chủ động cảnh báo cho bạn khi cần hành động.",
    Component: PortfolioReadyBody,
  },
];

const OnboardingLoading = ({ step, onNext, answers, loadingText }) => {
  const currentStepConfig = LOADING_STEPS[step] || LOADING_STEPS[0];
  const {
    id,
    isReady: isReadyStep,
    title,
    subtitle,
    Component,
  } = currentStepConfig;
  const navigate = useNavigate();
  const currentIndex = LOADING_STEPS.findIndex((s) => s.id === id);

  const getStepState = (idx) => {
    if (isReadyStep || currentIndex > idx) return "completed";
    if (currentIndex === idx) return "active";
    return "todo";
  };

  return (
    <div className="flex flex-col items-center w-full max-w-[542px] relative">
      <div className="w-full flex flex-col items-center mb-[32px] shrink-0">
        <div className="w-full flex justify-between relative max-w-[481px]">
          {LOADING_STEPS.slice(0, -1).map((_, idx) => (
            <div
              key={`line-${idx}`}
              className="absolute h-[1px] w-[127px] bg-[#ececed] -z-10"
              style={{ top: "20px", left: `${82 + idx * 191}px` }}
            >
              <div
                className="h-full bg-[#3c3c43] transition-all duration-500"
                style={{
                  width: getStepState(idx) === "completed" ? "100%" : "0%",
                }}
              />
            </div>
          ))}

          {LOADING_STEPS.map((step, idx) => {
            const style = STEP_STATE_STYLE[getStepState(idx)];

            return (
              <div
                key={step.id}
                className="flex flex-col items-center gap-[8px] w-[100px]"
              >
                <div
                  className={`w-[40px] h-[40px] rounded-full flex items-center justify-center border transition-all duration-300 ${style.circle}`}
                >
                  {style.indicator(idx)}
                </div>
                <span
                  className={`text-[12px] font-medium transition-colors duration-300 text-center text-[#636369]`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isReadyStep && (
          <motion.div
            key="ready"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full flex flex-col items-center max-w-[480px] gap-[24px] mt-[13%]"
          >
            <TitleSection title={title} subtitle={subtitle} />
            <div className="w-full">
              <Component answers={answers} />
            </div>
            <button
              onClick={onNext}
              className="mt-[16%] w-[244px] h-[48px] rounded-[12px] bg-[#3c3c43] text-white text-[16px] leading-[24px] font-medium hover:opacity-90 active:opacity-80 transition-all shrink-0 mx-auto"
            >
              Khám phá danh mục ngay
            </button>
            <p className="text-[14px] leading-[20px] text-[#a0a0a5] text-center -mt-[8px] mb-[20px]">
              Nebula sẽ thông báo ngay khi có tín hiệu mới.
            </p>
          </motion.div>
        )}

        {!isReadyStep && (
          <motion.div
            key="loading-container"
            className="w-full flex flex-col items-center mt-[8.75%] mb-[8.19%]"
          >
            <div className="w-full flex flex-col items-center justify-end mb-[16px] gap-[24px]">
              <TitleSection title={title} subtitle={subtitle} />
              <AnimatePresence mode="wait">
                <motion.div
                  key={id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full"
                >
                  <Component />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-[8px] mb-[12px] h-[24px]">
              <LoadingSpinner />
              <span className="text-[14px] text-[#111827]">{loadingText}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!isReadyStep && (
        <>
          <p className="text-[16px] text-[#636369] text-center mt-auto">
            Đừng lo nếu bạn muốn khám phá tính năng khác trước. Nebula vẫn đang
            chạy ngầm và sẽ sẵn sàng khi bạn quay lại.
          </p>

          <button
            onClick={() => {
              navigate("/");
            }}
            className="mt-[16px] h-[48px] px-[32px] mb-[20px] rounded-[12px] border border-[#ececed] bg-white text-[16px] font-medium text-[#111827] hover:bg-[#f3f4f6] transition-colors shrink-0"
          >
            Về trang chủ
          </button>
        </>
      )}
    </div>
  );
};

export default OnboardingLoading;
